import React, { useState, useRef, useEffect } from 'react';
import Navbar from '../../Components/Common/Navbar';
import Footer from '../../Components/Common/Footer';
import { Container } from '../../Components/ui/Container';
import { Button } from '../../Components/ui/Button';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { CustomSelect, CustomSelectItem } from '../../Components/Common/CustomSelect';
import { Label } from '../../Components/ui/Label';
import { DatePicker } from '../../Components/Common/DatePicker';
import { cn } from '../../lib/cn';
import { Check, X, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { getImagePath } from '../../Utils/Utilities';
import apiService from '../../Utils/APIService';

const BUSINESS_CATEGORIES = [
    { value: 'manufacturer', label: 'Manufacturer' },
    { value: 'trader', label: 'Trader / Wholesaler' },
    { value: 'service', label: 'Service Provider' },
    { value: 'retailer', label: 'Retailer' }
];

const BusinessCategoryMultiSelect = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleCategory = (categoryValue) => {
        onChange(prev =>
            prev.includes(categoryValue) 
                ? prev.filter(c => c !== categoryValue) 
                : [...prev, categoryValue]
        );
    };

    const selectedLabels = value.map(v => 
        BUSINESS_CATEGORIES.find(cat => cat.value === v)?.label
    ).filter(Boolean);

    return (
        <div className="relative mt-3" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full h-11 px-3 py-2 rounded-[5px] border-2 border-border transition-all bg-white text-text-primary hover:border-primary focus:border-primary focus:outline-none focus:ring-0 flex items-center justify-between"
            >
                <span className="text-[16px] text-left">
                    Select categories
                </span>
                <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
            </button>

            {isOpen && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-border rounded-[5px] shadow-lg">
                    <div className="p-2">
                        {BUSINESS_CATEGORIES.map((category) => (
                            <button
                                key={category.value}
                                onClick={() => toggleCategory(category.value)}
                                className="w-full flex items-center gap-3 p-2 rounded-[5px] hover:bg-background-light transition-colors text-left"
                            >
                                <div className={cn(
                                    "w-5 h-5 border-2 rounded flex items-center justify-center flex-shrink-0 transition-colors",
                                    value.includes(category.value)
                                        ? "bg-primary border-primary"
                                        : "bg-white border-border"
                                )}>
                                    {value.includes(category.value) && (
                                        <i className="fas fa-check text-white text-xs"></i>
                                    )}
                                </div>
                                <span className="font-medium text-[14px]">{category.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const DemoBookingFlow = ({ onComplete, onBack, hideInitialBack, brand }) => {
    const isMobile = useIsMobile();

    const [businessCategories, setBusinessCategories] = useState([]);
    const [whatToSell, setWhatToSell] = useState('');
    const [teamSize, setTeamSize] = useState('');
    const [currentSoftware, setCurrentSoftware] = useState('');
    const [selectedModules, setSelectedModules] = useState([]);
    const [demoDate, setDemoDate] = useState(null);
    const [demoTime, setDemoTime] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showBookingLoader, setShowBookingLoader] = useState(false);

    const getUserInfo = () => {
        const allDataRaw = localStorage.getItem('allData');
        const urlParams = new URLSearchParams(window.location.search);
        
        let name = '';
        let business = '';
        let mobile = urlParams.get('i') || '';
        
        if (allDataRaw) {
            try {
                const allData = JSON.parse(allDataRaw);
                name = (allData?.firstName || '') + ' ' + (allData?.lastName || '');
                business = allData?.companyName || '';
                mobile = allData.mobile || mobile;
            } catch (error) {
                console.error('Failed to parse user info');
            }
        }
        
        return { name, business, mobile };
    };

    const userInfo = getUserInfo();

    const toggleModule = (module) => {
        setSelectedModules(prev =>
            prev.includes(module) ? prev.filter(m => m !== module) : [...prev, module]
        );
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return "--";
        const date = new Date(dateString);
        const currentYear = new Date().getFullYear();
        const targetYear = date.getFullYear();
        const day = date.getDate();
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = monthNames[date.getMonth()];
        if (targetYear !== currentYear) {
            return `${day}-${month}-${targetYear.toString().slice(-2)}`;
        }
        return `${day}-${month}`;
    };

    const isFormValid = () => {
        return businessCategories.length > 0 && 
               whatToSell.trim() && 
               teamSize && 
               currentSoftware.trim() && 
               selectedModules.length > 0 && 
               demoDate && 
               demoTime.trim();
    };

    // BOOKING LOADER
    if (showBookingLoader) {
        return (
            <div className="mb-12 max-w-2xl mx-auto text-center py-16">
                <div className="flex flex-col items-center gap-6">
                    <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full border-4 border-border"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                    </div>
                    <div>
                        <p className="text-[18px] md:text-[22px] font-semibold text-text-primary mb-1">
                            Booking your demo...
                        </p>
                        <p className="text-sm text-text-secondary">
                            Please wait a moment
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // SINGLE FORM VIEW
    return (
        <>
            <div className="mb-12 max-w-4xl mx-auto">

                <div className="space-y-4 md:space-y-5">
                    {/* User Info Panel - Disabled */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-4 md:p-5">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                            <div>
                                <Label>Name</Label>
                                <div className="border-2 border-border rounded-[5px] overflow-hidden mt-2">
                                    <input
                                        type="text"
                                        value={userInfo.name}
                                        disabled
                                        className="w-full h-11 px-3 py-2 transition-all bg-white text-gray-500 focus:outline-none cursor-not-allowed text-[16px] focus:ring-0"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Business</Label>
                                <div className="border-2 border-border rounded-[5px] overflow-hidden mt-2">
                                    <input
                                        type="text"
                                        value={userInfo.business}
                                        disabled
                                        className="w-full h-11 px-3 py-2 transition-all bg-white text-gray-500 focus:outline-none cursor-not-allowed text-[16px] focus:ring-0"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Mobile</Label>
                                <div className="border-2 border-border rounded-[5px] overflow-hidden mt-2">
                                    <input
                                        type="text"
                                        value={userInfo.mobile}
                                        disabled
                                        className="w-full h-11 px-3 py-2 transition-all bg-white text-gray-500 focus:outline-none cursor-not-allowed text-[16px] focus:ring-0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Business Information */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-4 md:p-5">
                        <div className="space-y-4">
                            {/* Business Category */}
                            <div>
                                <Label mandatory={true}>What's your business category?</Label>
                                <BusinessCategoryMultiSelect 
                                    value={businessCategories} 
                                    onChange={setBusinessCategories} 
                                />
                            </div>

                            {/* What do you sell */}
                            <div>
                                <Label mandatory={true}>What do you sell?</Label>
                                <div className="border-2 border-border rounded-[5px] overflow-hidden mt-2">
                                    <input
                                        type="text"
                                        value={whatToSell}
                                        onChange={(e) => setWhatToSell(e.target.value)}
                                        placeholder="e.g. Electronics"
                                        className="w-full h-11 px-3 py-2 transition-all bg-white text-text-primary focus:outline-none focus:ring-0 placeholder:text-[16px] text-[16px] placeholder:text-gray-400"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Team Information */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-4 md:p-5">
                        <div className="grid grid-cols-1 md:grid-cols-[30%_1fr] gap-4 md:gap-5">
                            {/* Team Size */}
                            <div>
                                <Label mandatory={true}>How big is your team?</Label>
                                <CustomSelect value={teamSize} onValueChange={setTeamSize} placeholder="Team Size">
                                    <CustomSelectItem value="1-5">1-5 employees</CustomSelectItem>
                                    <CustomSelectItem value="6-10">6-10 employees</CustomSelectItem>
                                    <CustomSelectItem value="11-20">11-20 employees</CustomSelectItem>
                                    <CustomSelectItem value="21-50">21-50 employees</CustomSelectItem>
                                    <CustomSelectItem value="51-100">51-100 employees</CustomSelectItem>
                                    <CustomSelectItem value="100+">100+ employees</CustomSelectItem>
                                </CustomSelect>
                            </div>
                            {/* Current Software */}
                            <div>
                                <Label mandatory={true}>What ERP/CRM software do you currently use?</Label>
                                <div className="border-2 border-border rounded-[5px] overflow-hidden mt-2">
                                    <input
                                        type="text"
                                        value={currentSoftware}
                                        onChange={(e) => setCurrentSoftware(e.target.value)}
                                        placeholder="e.g. Tally"
                                        className="w-full h-11 px-3 py-2 transition-all bg-white text-text-primary focus:outline-none focus:ring-0 placeholder:text-[16px] text-[16px] placeholder:text-gray-400"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Modules Selection */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-4 md:p-5">
                        <Label mandatory={true}>What modules should we show in the demo?</Label>
                        {/* <p className="text-sm text-text-secondary mb-3">Select the modules you are interested in</p> */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5 md:gap-2">
                            {/* {[
                                'CRM', 'Quotes', 'Orders', 'Billing',
                                'E-Way Bill', 'E-Invoice', 'Accounts', 'GSTR1 & 3B',
                                'Recovery', 'Inventory', 'Batches', 'BOM',
                                'Purchase Orders', 'Suppliers Invoices', 'MRP', 'Projects',
                                'Manufacturing', 'Tasks', 'Support Ticketing', 'Contracts'
                            ].map((module) => ( */}
                            {[
                                'CRM', 'Quotes', 'Orders', 'Billing', 'Accounts', 'Inventory', 'Manufacturing'
                            ].map((module) => (
                                <button
                                    key={module}
                                    onClick={() => toggleModule(module)}
                                    className={cn(
                                        "p-1.5 md:p-2 rounded-[5px] border-2 transition-all text-[13px] md:text-sm font-semibold text-center",
                                        selectedModules.includes(module)
                                            ? "bg-primary text-white border-primary"
                                            : "bg-white border-border text-text-primary hover:border-primary"
                                    )}
                                >
                                    {module}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Schedule Demo */}
                    <div className="bg-background-light border-2 border-border rounded-[5px] p-4 md:p-5">
                        {/* <p className="text-[16px] text-text-primary mb-4">
                            Our team would be happy to show you how {brand === 'bz' ? 'Biziverse' : 'QikERP'} can transform your business operations.
                        </p> */}
                        <div className="grid grid-cols-1 md:grid-cols-[35%_1fr] gap-4 md:gap-5">
                            <div>
                                <Label mandatory={true}>Demo Date</Label>
                                <DatePicker
                                    value={demoDate}
                                    onChange={setDemoDate}
                                    placeholder="25-Feb"
                                    fromYear={2024}
                                    toYear={2030}
                                    disablePastDates={true}
                                    disableSundays={true}
                                />
                            </div>
                            <div>
                                <Label mandatory={true}>Approximate Time</Label>
                                <div className="border-2 border-border rounded-[5px] overflow-hidden mt-2">
                                    <input
                                        type="text"
                                        value={demoTime}
                                        onChange={(e) => setDemoTime(e.target.value)}
                                        placeholder="e.g. Around 11 AM"
                                        className="w-full h-11 px-3 py-2 transition-all bg-white text-text-primary focus:outline-none focus:ring-0 placeholder:text-[16px] text-[16px] placeholder:text-gray-400"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-8 flex items-center justify-center gap-4">
                    <Button
                        onClick={() => setShowConfirmDialog(true)}
                        size={!isMobile ? "lg" : "default"}
                        disabled={!isFormValid()}
                    >
                        Book Demo
                    </Button>
                    {!hideInitialBack && (
                        <Button onClick={onBack} variant="outline">
                            <i className="fas fa-arrow-left mr-2"></i>Back
                        </Button>
                    )}
                </div>
            </div>

            {showConfirmDialog && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowConfirmDialog(false)}>
                    <div className="bg-white w-full max-w-md rounded-lg shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 pt-5 pb-3">
                            <h2 className="text-[20px] font-bold text-text-primary">Confirm Demo Booking</h2>
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none"
                            >
                                &times;
                            </button>
                        </div>

                        {/* Scheduled Card */}
                        <div className="mx-5 border-2 border-primary bg-primary-subtle rounded p-4">
                            <p className="text-text-primary font-semibold text-base">
                                Schedule demo for <span className='font-bold'>{demoDate ? formatDateForDisplay(demoDate) : 'N/A'} @ {demoTime}</span>
                            </p>
                            <p className="text-text-secondary text-sm mt-3">Click the button to confirm.</p>
                        </div>

                        {/* Details */}
                        <div className="mx-5 mt-3 pb-1 space-y-2 text-text-primary">
                            <p>
                                A <span className="font-bold">{businessCategories.map(c => ({ manufacturer: 'Manufacturer', trader: 'Trader / Wholesaler', service: 'Service Provider', retailer: 'Retailer' }[c] || c)).join(', ')}</span> business selling <span className="font-bold">{whatToSell}</span>.
                            </p>
                            {teamSize && (
                                <p>Team of <span className="font-bold">{teamSize}</span> employees{currentSoftware ? <>, currently using <span className="font-bold">{currentSoftware}</span></> : ''}.</p>
                            )}
                            {selectedModules.length > 0 && (
                                <p>Interested in: <span className="font-bold">{selectedModules.join(', ')}</span>.</p>
                            )}
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-3 px-5 py-4">
                            <Button
                                size="sm"
                                onClick={async () => {
                                    const urlParams = new URLSearchParams(window.location.search);
                                    const i = urlParams.get('i') || '';
                                    const o = urlParams.get('o') || '';
                                    const summary = {
                                        categories: businessCategories.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(', '),
                                        product: whatToSell,
                                        team: teamSize,
                                        software: currentSoftware,
                                        modules: selectedModules.join(', '),
                                        date: demoDate ? format(demoDate, "dd-MMM-yy") : '',
                                        time: demoTime
                                    };
                                    setShowConfirmDialog(false);
                                    setShowBookingLoader(true);
                                    try {
                                        await apiService.setDemoBookingWsp(i, o, JSON.stringify(summary));
                                    } catch (error) {
                                        console.error("Failed to book demo alert");
                                    }
                                    setTimeout(() => {
                                        setShowBookingLoader(false);
                                        if (onComplete) onComplete();
                                    }, 2200);
                                }}
                            >
                                <Check className="w-6 h-6 mr-1" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowConfirmDialog(false)}
                            >
                                <X className="w-6 h-6 mr-1" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const BookDemo = ({ brand }) => {
    const isMobile = useIsMobile();
    const [showThankYou, setShowThankYou] = useState(false);

    if (showThankYou) {
        return (
            <div className="min-h-screen bg-white pt-12 md:pt-16 flex flex-col">
                <Navbar logo={getImagePath("/Images/QikERP.png")} />
                <main className="flex-grow flex items-center">
                    <section className="bg-white pt-8 md:pt-16 lg:pt-20 w-full">
                        <Container>
                            <div className="text-center py-12">
                                <div className="mb-6">
                                    <i className="fas fa-check-circle text-6xl text-primary"></i>
                                </div>
                                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                                    Thank You!
                                </h2>
                                <p className="text-lg text-text-primary">
                                    We appreciate your interest. Our team will contact you shortly.
                                </p>
                            </div>
                        </Container>
                    </section>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white pt-12 md:pt-16 flex flex-col">
            <Navbar logo={getImagePath("/Images/QikERP.png")} />
            <main className={`flex-grow flex ${isMobile ? 'items-center' : ''}`}>
                <section className="bg-white pt-8 md:pt-16 lg:pt-20 w-full">
                    <Container>
                        <DemoBookingFlow
                            onComplete={() => setShowThankYou(true)}
                            onBack={() => window.history.back()}
                            brand={brand}
                        />
                    </Container>
                </section>
            </main>
            <Footer className="!bg-background-light" />
        </div>
    );
};

export default BookDemo;